import { useState } from "react";
import axios from "axios";
import { getApiUrl } from "../../../../config/api";
import { t, formatDate } from "../shared/trendReportShared";

// Queues a title/notes/PDF as must-cover news for the NEXT day's report
// (backend always targets upload-date + 1, Taipei - see
// trend_reports_services.add_priority_item). Visual language borrowed from
// the product PDF uploader (drag-and-drop card, gradient submit button) since
// this is the same "attach a PDF, get it processed" interaction.
//
// TrendReportsFeedPage.js owns the GET (queue prop) and the auth session
// (currentUser prop); this file only performs the write actions (POST/DELETE)
// a user triggers here, then calls onQueueChange so the parent re-fetches.
export default function CustomizationTab({ language, currentUser, queue, onQueueChange }) {
  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");
  const [pdfFile, setPdfFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const authHeaders = async () => {
    const token = await currentUser.getIdToken();
    return { Authorization: `Bearer ${token}` };
  };

  const canSubmit = Boolean(title.trim() || notes.trim() || pdfFile);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError("");
    try {
      const headers = await authHeaders();
      const form = new FormData();
      if (title.trim()) form.append("title", title.trim());
      if (notes.trim()) form.append("notes", notes.trim());
      if (pdfFile) form.append("pdf", pdfFile);
      const res = await axios.post(
        `${getApiUrl()}/api/v2/trend-reports/priority-items`,
        form,
        { headers },
      );
      if (!res.data?.success) {
        setError(res.data?.error || t("priorityUploadError", language));
        return;
      }
      setTitle("");
      setNotes("");
      setPdfFile(null);
      await onQueueChange();
    } catch (e) {
      setError(e.response?.data?.error || t("priorityUploadError", language));
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemove = async (index) => {
    if (!queue.reportDate) return;
    try {
      const headers = await authHeaders();
      await axios.delete(
        `${getApiUrl()}/api/v2/trend-reports/priority-items/${queue.reportDate}/${index}`,
        { headers },
      );
      await onQueueChange();
    } catch (e) {
      // best-effort - the item just stays in the list, user can retry
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const dropped = e.dataTransfer.files?.[0];
    if (dropped && dropped.type === "application/pdf") {
      setPdfFile(dropped);
    }
  };

  return (
    <div className="animate-fadeIn max-w-2xl mx-auto space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-8 relative overflow-hidden">
        {/* Decorative elements, matching the product PDF uploader */}
        <div className="absolute top-0 right-0 w-40 h-40 bg-amber-500/5 rounded-full -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-amber-500/5 rounded-full translate-y-1/2 -translate-x-1/2" />

        <div className="relative mb-6 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-500/10 rounded-2xl mb-4">
            <svg
              className="w-8 h-8 text-amber-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            {t("priorityUploadTitle", language)}
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            {t("priorityUploadHint", language)}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="relative space-y-4">
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t("priorityTitleLabel", language)}
            className="w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/40 px-4 py-3 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-amber-400/60"
          />
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={t("priorityNotesLabel", language)}
            rows={3}
            className="w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/40 px-4 py-3 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-amber-400/60"
          />

          {/* Drop zone */}
          <div
            className={`relative border-2 border-dashed rounded-2xl p-8 text-center transition-all duration-300 cursor-pointer ${
              pdfFile
                ? "border-amber-500/50 bg-amber-500/5"
                : "border-gray-300 dark:border-gray-600 hover:border-amber-500/50 hover:bg-gray-50 dark:hover:bg-gray-700/30"
            }`}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={() => document.getElementById("priority-pdf-upload").click()}
          >
            <div
              className={`mb-3 inline-flex items-center justify-center w-12 h-12 rounded-2xl transition-all duration-300 ${
                pdfFile ? "bg-amber-500/20" : "bg-gray-100 dark:bg-gray-700"
              }`}
            >
              {pdfFile ? (
                <svg className="w-6 h-6 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
              ) : (
                <svg
                  className="w-6 h-6 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                  />
                </svg>
              )}
            </div>
            <div className="text-sm text-gray-700 dark:text-gray-300">
              {pdfFile ? (
                <span className="text-amber-600 dark:text-amber-400 font-medium">
                  {pdfFile.name}
                </span>
              ) : (
                <span className="text-amber-600 dark:text-amber-400 font-medium">
                  {t("priorityPdfLabel", language)}
                </span>
              )}
            </div>
            <input
              id="priority-pdf-upload"
              type="file"
              accept="application/pdf"
              onChange={(e) => setPdfFile(e.target.files?.[0] || null)}
              className="hidden"
            />
          </div>

          {pdfFile && (
            <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 rounded-xl p-3 animate-fadeIn">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 shrink-0 bg-red-500/10 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                    {pdfFile.name}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {(pdfFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setPdfFile(null);
                }}
                className="shrink-0 p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-lg transition-all"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          )}

          <button
            type="submit"
            disabled={submitting || !canSubmit}
            className={`w-full py-3.5 px-6 rounded-xl font-medium text-sm transition-all duration-300 flex items-center justify-center gap-2 ${
              canSubmit
                ? "bg-gradient-to-r from-amber-500 to-amber-600 text-white hover:from-amber-600 hover:to-amber-700 shadow-lg shadow-amber-500/20 hover:shadow-amber-500/40"
                : "bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed"
            }`}
          >
            {submitting && (
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
            )}
            {submitting
              ? t("priorityQueuing", language)
              : t("priorityQueueButton", language)}
          </button>

          {error && <p className="text-xs text-red-500 text-center">{error}</p>}
        </form>
      </div>

      {/* Queued items */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-xs font-bold uppercase tracking-wide text-gray-400 mb-3">
          {t("priorityQueuedItems", language)}{" "}
          {queue.reportDate ? formatDate(queue.reportDate, language) : ""}
        </h3>
        {queue.status === "loading" && (
          <p className="text-xs text-gray-400">{t("loading", language)}</p>
        )}
        {queue.status === "ok" && queue.items.length === 0 && (
          <p className="text-xs text-gray-400">{t("priorityNoneQueued", language)}</p>
        )}
        {queue.status === "ok" && queue.items.length > 0 && (
          <ul className="space-y-2">
            {queue.items.map((it, i) => (
              <li
                key={i}
                className="flex items-start justify-between gap-3 rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-700/60 px-3 py-2.5"
              >
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate">
                    {it.title || it.notes?.slice(0, 60) || "Untitled"}
                  </p>
                  <p className="text-[11px] text-gray-400">
                    {it.uploaded_by || ""}
                    {it.pdf_url && (
                      <>
                        {" "}
                        ·{" "}
                        <a
                          href={it.pdf_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-amber-600 dark:text-amber-400 hover:underline"
                        >
                          PDF
                        </a>
                      </>
                    )}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => handleRemove(i)}
                  className="shrink-0 text-[11px] font-semibold text-gray-400 hover:text-red-500"
                >
                  {t("priorityRemove", language)}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
